import { DownOutlined } from '@ant-design/icons';
import { useLocalStorageState, useSetState } from 'ahooks';
import { Card, Radio, Select, Tabs, Tooltip } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio/interface';
import classnames from 'classnames';
import { isNil } from 'lodash-es';
import moment from 'moment';
import React, { FC, useEffect, useState } from 'react';
import DownloadLine from '@/assets/images/icons/download-line.react.svg?react';
import { ReportGuideType } from '@/config/app/auth';
import { exportCSV } from '@/infrastructure/export-csv';
import { isAndroid, isHarmonyOS } from '@/infrastructure/product-info';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { reportClick, ReportScene } from '@/infrastructure/reporter/datong/report';
import { DATE_TIME_FORMAT, getDateRangeByShortCut } from '@/infrastructure/utils/timestamp';
import { HintRow } from '@/presentation/components/exception-issue-list/hint-row';
import { NumberType } from '@/presentation/components/form/components/select-number';
import FreeProductGuide, { Display, GuideType } from '@/presentation/components/free-product-guide';
import { hintIconText } from '@/presentation/components/hint-text/hintText';
import styles from '@/presentation/components/issue-trend-analysis/index.module.scss';
import { Chart, RATE } from '@/presentation/components/issue-trend-analysis/line-chart';
import { PermNodeValue } from '@/presentation/components/permission-manage/config';
import { GetDashboardData, MetricName } from '@/presentation/components/trend/api';
// import { useBatchPermission } from '@/presentation/hooks/role/role-list';
import { ANR, FREEZE } from '@/presentation/config/name';
import { ISSUE_TREND_ANALYSIS_VISIBLE } from '@/presentation/config/storage';
import { useBatchPermission } from '@/presentation/hooks/role/role-list';
import { useStores } from '@/presentation/store';
import { PropsWithStyles } from '@/proto/common';
import { getIssueTypeMapLabel } from '@/proto/field';
import { IssueType } from '@/proto/issue';

import { IGetDashboardDataRsp, MetricSubType, MetricType, TimeType } from '@/proto/metric';
import { fromPBTime } from '@raft/utils';

interface IProps {
  cardProps?: any;
  showTools?: boolean;
  issueType: IssueType;
  metricType: MetricType;
  token: string;
  headToken: string;
  pgId: string;
  timeWindow?: TimeType;
  startDateStr?: string;
  endDateStr?: string;
  launchDuration?: NumberType;
  searchParams?: Record<string, any>;
  queryCompareToken?: (params: Record<string, any>) => Promise<string>;
  includeMetrics?: MetricSubType[];
  defaultTab?: any;
  uuidFetch?: string;
  uuidControl?: boolean;
}

const { TabPane } = Tabs;

enum UnitEnum {
  Hundred=0,
  Thousand=1,
  TenThousand=2
}

const options = [
  {
    label: '百分比',
    value: UnitEnum.Hundred,
  },
  {
    label: '千分比',
    value: UnitEnum.Thousand,
  },
  {
    label: '万分比',
    value: UnitEnum.TenThousand,
  },
];


const subTypes = [
  MetricSubType.SUB_TYPE_DEVICE_RATIO,
  MetricSubType.SUB_TYPE_AVG_DEVICE_RATIO,
  MetricSubType.SUB_TYPE_COUNT_RATIO,
  MetricSubType.SUB_TYPE_USER_RATIO,
  MetricSubType.SUB_TYPE_COUNT,
  MetricSubType.SUB_TYPE_LAUNCH_COUNT,
  MetricSubType.SUB_TYPE_DEVICE_UV,
  MetricSubType.SUB_TYPE_USER_UV,
  MetricSubType.SUB_TYPE_DAU_DEVICE_UV,
];

export const RATE_LIST = [
  MetricSubType.SUB_TYPE_DEVICE_RATIO,
  MetricSubType.SUB_TYPE_COUNT_RATIO,
  MetricSubType.SUB_TYPE_USER_RATIO,
  MetricSubType.SUB_TYPE_AVG_DEVICE_RATIO,
];
export const IssueTrendAnalysis: FC<PropsWithStyles<IProps>> = (props) => {
  const {
    issueType, metricType, token, timeWindow,
    headToken, launchDuration, startDateStr,
    endDateStr, pgId, searchParams, queryCompareToken,
    showTools = true, includeMetrics = [], defaultTab,
  } = props;
  const { isFreeProduct, isPaidProduct } = useStores().ProductStore as any;
  const isFree = isFreeProduct();
  const isPaid = isPaidProduct();
  const [storageVisible, setStorageVisible] = useLocalStorageState<boolean>(
    `${ISSUE_TREND_ANALYSIS_VISIBLE}-${pgId}`,
    {
      defaultValue: true,
    },
  );
  const [chartData, setChartData] = useState<IGetDashboardDataRsp>({});
  const { permission } = useBatchPermission([
    PermNodeValue.DauCount,
    PermNodeValue.QimeiCount,
    PermNodeValue.DauLaunchTimes,
    PermNodeValue.DauDeviceUV,
  ]);
  const [compareChartData, setCompareChartData] = useState<IGetDashboardDataRsp | undefined>(undefined);

  const isAndroidProduct = isAndroid();
  const isHarmonyProduct = isHarmonyOS();
  const issueTabType: {[key: string]: string} = {
    [IssueType.ISSUE_TYPE_CRASH]: '崩溃率',
    [IssueType.ISSUE_TYPE_ERROR]: '错误率',
    [IssueType.ISSUE_TYPE_ANR]: 'ANR率',
    [IssueType.ISSUE_TYPE_ANR_BUGLY]: `${isHarmonyProduct ? FREEZE : ANR}率`,
    [IssueType.ISSUE_TYPE_FOOM]: isAndroidProduct ? 'OOM率' : 'FOOM率',
    [IssueType.ISSUE_TYPE_FOOM_BUGLY]: isAndroidProduct ? 'OOM率' : 'FOOM率',
  };
  const [state, setState] = useSetState({
    unit: defaultTab?.unit ?? UnitEnum.Hundred,
    statisticalDimension: false,
    tabKey: defaultTab?.tabKey ?? MetricSubType.SUB_TYPE_DEVICE_RATIO,
    loading: false,
    tabName: defaultTab?.tabName ?? `设备${issueTabType[issueType]}`,
  });
  const unitChange = (value: number) => {
    setState({ unit: value });
  };

  const statisticalDimensionChange = (e: RadioChangeEvent) => {
    setState({ statisticalDimension: e.target.value });
    getChartData(headToken, e.target.value);
  };

  // token控制，保证两个模式不冲突
  useEffect(() => {
    if (token && headToken && !props.uuidControl) {
      getChartData(headToken, state.statisticalDimension);
    }
  }, [headToken]);

  // uuid控制
  useEffect(() => {
    if (props.uuidControl && props.uuidFetch && token && headToken) {
      getChartData(headToken, state.statisticalDimension);
    }
  }, [props.uuidFetch]);

  // 获取趋势分析数据
  const getChartData = async (token: string, statisticalDimension: boolean) => {
    setState({ loading: true });
    const mainTotalize = timeWindow
    && [TimeType.MINUTE5, TimeType.HOUR].includes(timeWindow)
      ? statisticalDimension : false;

    // 对比数据的统计维度逻辑：只有在对比时间窗口是5分钟或1小时时才使用用户选择的统计维度，否则强制为非累计
    const compareTotalize = searchParams?.timeWindowCompare
        && [TimeType.MINUTE5, TimeType.HOUR].includes(searchParams.timeWindowCompare)
      ? statisticalDimension
      : false;

    const total = mainTotalize && compareTotalize;


    const params = { metricType, totalize: searchParams?.isCompareOpen ? total : mainTotalize, subTypes: includeMetrics
      ? subTypes?.filter(key => includeMetrics.includes(key)) : subTypes, token };
    const data = await GetDashboardData(params);

    if (searchParams?.isCompareOpen) {
      const values: Record<string, any> = { ...searchParams };
      Object.keys(searchParams).filter(item => (item.includes('Compare') || item.includes('compare')))
        .forEach((item) => {
          values[item.replace('Compare', '').replace('_compare', '')] = searchParams[item];
        });
      if (values.customDate) {
        const dateRange = getDateRangeByShortCut(values.customDate);
        values.startDateStr = dateRange[0].format(DATE_TIME_FORMAT);
        values.endDateStr = dateRange[1].format(DATE_TIME_FORMAT);
        values.startTime = dateRange[0].unix().toString();
        values.endTime =  dateRange[1].unix().toString();
      } else {
        values.startDateStr = values.startDateStr || moment(values?.timeRange?.[0])
          .format(DATE_TIME_FORMAT);
        values.endDateStr = values.endDateStr || moment(values?.timeRange?.[1])
          .format(DATE_TIME_FORMAT);
        values.startTime = moment(values.startDateStr).unix()
          .toString();
        values.endTime = moment(values.endDateStr).unix()
          .toString();
      }


      const compareToken = await queryCompareToken?.(values);


      const data = await GetDashboardData({ metricType, totalize: total, subTypes, token: compareToken });
      setCompareChartData(data);
    } else {
      setCompareChartData(undefined);
    }
    setState({ loading: false });
    setChartData(data);
  };


  const getTabName = (value: number): string => {
    switch (value) {
      case MetricSubType.SUB_TYPE_DEVICE_RATIO:
        return `设备${issueTabType[issueType]}`;
      case MetricSubType.SUB_TYPE_AVG_DEVICE_RATIO:
        return `设备平均${issueTabType[issueType]}`;
      case MetricSubType.SUB_TYPE_COUNT_RATIO:
        return `次数${issueTabType[issueType]}`;
      case MetricSubType.SUB_TYPE_USER_RATIO:
        return `用户${issueTabType[issueType]}`;
      case MetricSubType.SUB_TYPE_COUNT:
        return '发生次数';
      case MetricSubType.SUB_TYPE_LAUNCH_COUNT:
        return '启动次数';
      case MetricSubType.SUB_TYPE_DEVICE_UV:
        return '影响设备数';
      case MetricSubType.SUB_TYPE_USER_UV:
        return '影响用户数';
      case MetricSubType.SUB_TYPE_DAU_DEVICE_UV:
        return '联网设备数';
      default:
        return '';
    }
  };

  const tabChange = (value: string) => {
    const newValue = Number(value);
    setState({ tabKey: newValue, tabName: getTabName(newValue)  });
    reportClick(pgId, CUSTOM_ACTIONS.commonTrendChangeStatistics, {
      from: pgId,
      param3: value,
    });
  };

  const tabList = [
    { unit: '%', tab: hintIconText(`设备${issueTabType[issueType]}`, '设备异常率 =  满足筛选条件的异常设备数（按设备ID去重） /  满足联网上报支持的筛选条件的联网设备数（按设备ID去重）'), name: `设备${issueTabType[issueType]}`, key: MetricSubType.SUB_TYPE_DEVICE_RATIO },
    { unit: '%',
      tab: hintIconText(
        `设备平均${issueTabType[issueType]}`,
        '设备平均异常率 =  满足筛选条件的异常个例数 / 满足联网上报支持的筛选条件的联网设备数（按设备ID去重）',
      ),
      name: `设备平均${issueTabType[issueType]}`,
      key: MetricSubType.SUB_TYPE_AVG_DEVICE_RATIO,
      hidden: isFree,
    },
    { unit: '%', tab: hintIconText(`次数${issueTabType[issueType]}`, `次数异常率 =  满足筛选条件的异常个例数 / 满足筛选条件的启动次数。（当前启动次数的统计只支持时间范围和App版本两个筛选条件。）${isPaid ? '仅支持查看2023年7月29日之后的数据。' : ''}`), name: `次数${issueTabType[issueType]}`, key: MetricSubType.SUB_TYPE_COUNT_RATIO },
    { unit: '%',
      tab: hintIconText(
        `用户${issueTabType[issueType]}`,
        '人数异常率 =  满足筛选条件的异常用户数（按用户ID去重）/  满足联网上报支持的筛选条件的联网用户数（按用户ID去重）',
      ),
      name: `用户${issueTabType[issueType]}`,
      key: MetricSubType.SUB_TYPE_USER_RATIO,
      hidden: isFree,
    },
    {
      tab: '发生次数',
      name: '发生次数',
      key: MetricSubType.SUB_TYPE_COUNT,
    // hidden: !permission.get(PermNodeValue.DauCount),
    },
    {
      tab: hintIconText('启动次数', `从进程创建到进程退出计为一次启动。${isPaid ? '仅支持查看2023年7月29日之后的数据。' : ''}`),
      name: '启动次数',
      key: MetricSubType.SUB_TYPE_LAUNCH_COUNT,
      hidden: !permission.get(PermNodeValue.DauLaunchTimes) || isFree,
    },
    {
      tab: hintIconText('影响设备数', '异常上报中，按设备ID去重'),
      name: '影响设备数',
      key: MetricSubType.SUB_TYPE_DEVICE_UV,
    // hidden: !permission.get(PermNodeValue.QimeiCount),
    },
    {
      tab: hintIconText('影响用户数', '异常上报中，按用户ID去重'),
      name: '影响用户数',
      key: MetricSubType.SUB_TYPE_USER_UV,
      hidden: isFree,
    },
    {
      tab: hintIconText('联网设备数', '联网上报，按设备ID去重，当前只支持时间范围，APP版本，实验ID，业务下钻，BundleID，SDK版本等筛选字段的下钻。', 'topRight'),
      name: '联网设备数',
      key: MetricSubType.SUB_TYPE_DAU_DEVICE_UV,
      hidden: !permission.get(PermNodeValue.DauDeviceUV),
    },
  ].filter((item) => {
    if (includeMetrics.length) {
      return includeMetrics.includes(item.key) && !item.hidden;
    }

    return !item.hidden;
  });

  // 导出数据
  const exportData = async () => {
    const conditionA = compareChartData ? '(条件A)' : '';
    const timePoints = (chartData?.timePoints || []).map(timePoint => fromPBTime(timePoint).format('YYYY-MM-DD HH:mm:ss')) || [];
    const head = [[`时间范围${conditionA}`, ...tabList.map(item => `${item.name}${item.unit ? `（${item.unit}）` : ''}${conditionA}`)]];
    const body = [];
    const deviceRatio = chartData?.data?.find(item => item.metricName === MetricName.DeviceRatio)?.dataPoints;
    const avgDeviceUvRatio = chartData?.data?.find(item => item.metricName === MetricName.avgDeviceUvRatio)?.dataPoints;
    const countRatio = chartData?.data?.find(item => item.metricName === MetricName.CountRatio)?.dataPoints;
    const userUvRatio = chartData?.data?.find(item => item.metricName === MetricName.UserUvRatio)?.dataPoints;
    const count = chartData?.data?.find(item => item.metricName === MetricName.Count)?.dataPoints;
    const launchCount = chartData?.data?.find(item => item.metricName === MetricName.DauLaunchTimes)?.dataPoints;
    const deviceUv = chartData?.data?.find(item => item.metricName === MetricName.DeviceUv)?.dataPoints;
    const userUv = chartData?.data?.find(item => item.metricName === MetricName.UserUv)?.dataPoints;
    const dauDeviceUv = chartData?.data?.find(item => item.metricName === MetricName.DauDeviceUv)?.dataPoints;
    for (let i = 0; i < timePoints.length; i++) {
      body.push([
        timePoints[i],
        (deviceRatio?.[i] ? ((deviceRatio?.[i] * RATE[0])).toFixed(4) : ''),
        (avgDeviceUvRatio?.[i] ? ((avgDeviceUvRatio?.[i] * RATE[0])).toFixed(4) : ''),
        (countRatio?.[i] ? ((countRatio?.[i] * RATE[0])).toFixed(4) : ''),
        (userUvRatio?.[i] ? ((userUvRatio?.[i] * RATE[0])).toFixed(4) : ''),
        (count?.[i] || ''),
        (launchCount?.[i] || ''),
        (deviceUv?.[i] || ''),
        (userUv?.[i] || ''),
        (dauDeviceUv?.[i] || ''),
      ]);
    }
    // 导出条件B
    if (compareChartData) {
      head[0].push('时间范围(条件B)', ...tabList.map(item => `${item.name}${item.unit ? `（${item.unit}）` : ''}(条件B)`));
      const compareTimePoints = (compareChartData?.timePoints || []).map(timePoint => fromPBTime(timePoint).format('YYYY-MM-DD HH:mm:ss')) || [];
      const deviceRatio = compareChartData?.data?.find(item => item.metricName === MetricName.DeviceRatio)?.dataPoints;
      const avgDeviceUvRatio = compareChartData?.data?.find(item => item.metricName
        === MetricName.avgDeviceUvRatio)?.dataPoints;
      const countRatio = compareChartData?.data?.find(item => item.metricName === MetricName.CountRatio)?.dataPoints;
      const userUvRatio = compareChartData?.data?.find(item => item.metricName === MetricName.UserUvRatio)?.dataPoints;
      const count = compareChartData?.data?.find(item => item.metricName === MetricName.Count)?.dataPoints;
      const launchCount = compareChartData?.data?.find(item => item.metricName
        === MetricName.DauLaunchTimes)?.dataPoints;
      const deviceUv = compareChartData?.data?.find(item => item.metricName === MetricName.DeviceUv)?.dataPoints;
      const userUv = compareChartData?.data?.find(item => item.metricName === MetricName.UserUv)?.dataPoints;
      const dauDeviceUv = compareChartData?.data?.find(item => item.metricName === MetricName.DauDeviceUv)?.dataPoints;
      for (let i = 0; i < compareTimePoints.length; i++) {
        if (body[i]) {
          body[i].push(
            compareTimePoints[i],
            (deviceRatio?.[i] ? ((deviceRatio?.[i] * RATE[0])).toFixed(4) : ''),
            (avgDeviceUvRatio?.[i] ? ((avgDeviceUvRatio?.[i] * RATE[0])).toFixed(4) : ''),
            (countRatio?.[i] ? ((countRatio?.[i] * RATE[0])).toFixed(4) : ''),
            (userUvRatio?.[i] ? ((userUvRatio?.[i] * RATE[0])).toFixed(4) : ''),
            (count?.[i] || ''),
            (launchCount?.[i] || ''),
            (deviceUv?.[i] || ''),
            (userUv?.[i] || ''),
            (dauDeviceUv?.[i] || ''),
          );
        } else {
          body.push([
            ...Array.from({ length: 10 }, () => ''),
            compareTimePoints[i],
            (deviceRatio?.[i] ? ((deviceRatio?.[i] * RATE[0])).toFixed(4) : ''),
            (avgDeviceUvRatio?.[i] ? ((avgDeviceUvRatio?.[i] * RATE[0])).toFixed(4) : ''),
            (countRatio?.[i] ? ((countRatio?.[i] * RATE[0])).toFixed(4) : ''),
            (userUvRatio?.[i] ? ((userUvRatio?.[i] * RATE[0])).toFixed(4) : ''),
            (count?.[i] || ''),
            (launchCount?.[i] || ''),
            (deviceUv?.[i] || ''),
            (userUv?.[i] || ''),
            (dauDeviceUv?.[i] || ''),
          ]);
        }
      }
    }
    await exportCSV(`${getIssueTypeMapLabel(issueType)}-趋势分析`, head, body);
  };

  const showStatisticalDimension = () => {
    if (!timeWindow) {
      return false;
    }

    if (![TimeType.MINUTE5, TimeType.HOUR].includes(timeWindow)) {
      return false;
    }

    if (searchParams?.isCompareOpen) {
      if (!searchParams?.timeWindowCompare) {
        return false;
      }

      if (![TimeType.MINUTE5, TimeType.HOUR].includes(searchParams.timeWindowCompare)) {
        return false;
      }
    }

    return true;
  };


  const isHint = !isNil(typeof (launchDuration) === 'object' ? launchDuration?.value?.[0] : launchDuration) && startDateStr
    && endDateStr && (moment(startDateStr).isBefore('2023-05-17') || moment(endDateStr).isBefore('2023-05-17'));
  return <Card className={classnames(styles.card, props.className)} style={props.style} {...props.cardProps}>
    {showTools && <div className={styles.head}>
      <span className={styles.headTitle}>{searchParams?.isCompareOpen ? '趋势对比' : '趋势分析'}</span>
      <div>
        {(chartData?.data?.length && !isFree)
          ? <span className={styles.downloadIcon} onClick={exportData}><Tooltip
            title={'导出趋势数据'}><DownloadLine /></Tooltip></span> : null}
        {chartData?.data?.length && isFree ? (
          <span className={styles.freeDownloadIcon}>
            <FreeProductGuide
              popoverProps={{ overlayStyle: { width: 220 } }}
              guideType={GuideType.POP_CONFIRM}
              display={Display.INLINE}
              reportGuideType={ReportGuideType.Bubble}
              reportScene={ReportScene.TrendDownLoad}
              reportField={state.tabName}>
              <DownloadLine />
            </FreeProductGuide>
          </span>
        ) : null}
        <span className={styles.switch} onClick={() => {
          setStorageVisible(visible => !visible);
          reportClick(
            pgId,
            !storageVisible ? CUSTOM_ACTIONS.commonExpandTrendAnalysis : CUSTOM_ACTIONS.commonCloseTrendAnalysis,
            {
              from: pgId,
            },
          );
        }}>
          <span>{storageVisible ? '收起' : '展开'}</span>
          <DownOutlined className={classnames({
            [styles.headIcon]: true,
            [styles.up]: storageVisible,
            [styles.down]: !storageVisible,
          })} />
        </span>
      </div>
    </div>}
    {storageVisible ? <div className={styles.center}>
      <div className={styles.filters}>
        {RATE_LIST.includes(state.tabKey)
          ? <Select onChange={unitChange} value={state.unit} className={styles.unit} options={options} /> : null}
        {showStatisticalDimension()
          ? <div className={styles.statisticalDimension}>
            <label className={styles.label}>统计维度</label>
            <Radio.Group onChange={statisticalDimensionChange} value={state.statisticalDimension}>
              <Radio value={false}>{timeWindow === TimeType.MINUTE5 ? '5分钟' : '1小时'}</Radio>
              <Radio value={true}>累计</Radio>
            </Radio.Group>
          </div> : null}
      </div>
      <Tabs onChange={tabChange} activeKey={String(state.tabKey)} className={styles.tab}>
        {tabList.map(item => <TabPane tab={item.tab} key={item.key} />)}
      </Tabs>
    </div> : null}
    {isHint && storageVisible ? <HintRow /> : null}
    {storageVisible ? <Chart
      tabName={state.tabName}
      loading={state.loading}
      chartData={chartData}
      compareChartData={compareChartData}
      compareTimeWindow={searchParams?.timeWindowCompare}
      unit={state.unit}
      timeWindow={timeWindow}
      tabKey={state.tabKey}
    /> : null}

  </Card>;
};

